import { visit } from "unist-util-visit";
import type { Plugin } from "unified";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useNotebookContext } from "@/src/pages/study/hooks/useNotebook";
import { StudyUploadedDocument } from "@/src/pages/study/types";
import {
  getStudyNotebooksByNotebookIdDocumentsByDocumentIdChunksByChunkId,
  GetStudyNotebooksByNotebookIdDocumentsByDocumentIdChunksByChunkIdResponses,
} from "@/src/pages/study/client";
import { FileText, Quote } from "lucide-react";
import { CitationPopover } from "./citation-popover";
import * as Popover from "@radix-ui/react-popover";

const CITATION_REGEX = /\[\^(\d+)(?:[,-](\d+))?\]/g;

export const remarkCitations: Plugin = () => {
  return (tree) => {
    visit(tree, "text", (node, index, parent) => {
      if (!parent || typeof node.value !== "string") return;

      const matches = [...node.value.matchAll(CITATION_REGEX)];
      if (!matches.length) return;

      const newNodes: any[] = [];
      let lastIndex = 0;

      for (const match of matches) {
        const [full, docId, chunkId] = match;
        const start = match.index!;
        const end = start + full.length;

        // Text before citation
        if (start > lastIndex) {
          newNodes.push({
            type: "text",
            value: node.value.slice(lastIndex, start),
          });
        }

        // Footnote reference
        newNodes.push({
          type: "footnoteReference", // custom node
          identifier: chunkId ? `${docId}-${chunkId}` : `${docId}-1`,
          label: chunkId ? `${docId}-${chunkId}` : `${docId}-1`,
        });

        lastIndex = end;
      }

      // Trailing text
      if (lastIndex < node.value.length) {
        newNodes.push({
          type: "text",
          value: node.value.slice(lastIndex),
        });
      }

      parent.children.splice(index!, 1, ...newNodes);
    });
  };
};

function parseCitationFromAnchor(children: any) {
  const anchor = Array.isArray(children) ? children[0] : children;
  if (!anchor?.props?.href) return null;

  // #fn-1-2
  const match = anchor.props.href.match(/fn-(\d+)-(\d+)/);
  if (!match) return null;

  return {
    documentId: Number(match[1]),
    chunkIndex: Number(match[2]),
  };
}

export const Citation = ({ children, className, ...props }) => {
  const { notebook, selectedSources, navigatePage } = useNotebookContext();
  const citation = parseCitationFromAnchor(children);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<
    | GetStudyNotebooksByNotebookIdDocumentsByDocumentIdChunksByChunkIdResponses[200]
    | string
  >(null);

  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  const openNow = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    setOpen(true);
  };

  const closeLater = () => {
    closeTimeout.current = setTimeout(() => {
      setOpen(false);
    }, 120); // small grace period
  };

  const openSource = () => {
    if (!citation) return;
    const citationSource = selectedSources.filter(
      (s) => "document_id" in s && s.document_id === citation.documentId
    );
    if (!citationSource.length) return;
    navigatePage({ type: "source", value: citationSource[0] });
  };

  useEffect(() => {
    if (!citation || !open || data) return;
    const citationSource = selectedSources.filter(
      (s) => "document_id" in s && s.document_id === citation.documentId
    );
    if (!citationSource.length || !("id" in citationSource[0])) {
      setData("Source was deleted.");
    }
    getStudyNotebooksByNotebookIdDocumentsByDocumentIdChunksByChunkId({
      path: {
        notebook_id: notebook.id.toString(),
        document_id: citation.documentId.toString(),
        chunk_id: citation.chunkIndex.toString(),
      },
    })
      .then(({ data, error }) => {
        console.log(data);
        if (error)
          setData(
            "Failed to retrieve citation. The source may have been deleted."
          );
        else setData(data);
      })
      .catch((err) =>
        setData(
          "Failed to retrieve citation. The source may have been deleted."
        )
      );
  }, [open, citation, data]);

  if (!citation) {
    return <sup {...props}>{children}</sup>;
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild onMouseEnter={openNow} onMouseLeave={closeLater}>
        <sup
          className={cn(
            "inline-flex items-center gap-0.5",
            "rounded-full border border-blue-500",
            "bg-theme-0 px-1.5 py-1",
            "text-[10px] font-medium",
            "hover:bg-theme-1 hover:text-theme-0",
            "transition",
            "[&>a]:no-underline [&>a]:text-inherit cursor-pointer",
            "text-blue-500",
            className
          )}
          {...props}
        >
          <FileText className="h-3 w-3" />
          {children}
        </sup>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="top"
          align="center"
          sideOffset={8}
          className={cn(
            "z-[1000] w-80 rounded-lg border border-theme",
            "bg-theme-0 p-3 shadow-theme",
            "data-[state=open]:animate-in",
            "data-[state=closed]:animate-out"
          )}
          onMouseEnter={openNow}
          onMouseLeave={closeLater}
        >
          {data ? (
            typeof data === "string" ? (
              <span className="text-xs text-theme-2">{data}</span>
            ) : (
              <CitationPopover data={data} onOpenDocument={openSource} />
            )
          ) : (
            <span className="text-xs text-theme-2">Loading source…</span>
          )}

          {/* <Popover.Arrow className="text-theme-0" /> */}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
